<template>
  <div class="relative" id="product-gallery">
    <div class="w-full">
      <!-- Ana Görsel Alanı -->
      <div>
        <!-- Ana Görsel Container -->
        <div ref="galleryContainer"
          class="relative h-[500px] md:h-[600px] overflow-hidden bg-white group scroll-container touch-pan-y"
          :class="{ 'touch-none': scale > 1 }" @touchstart="handleTouchStart" @touchmove="handleTouchMove"
          @touchend="handleTouchEnd">
          <!-- Görsel Slider -->
          <div class="flex h-full transform-gpu will-change-transform" :style="sliderStyle">
            <div v-for="(image, idx) in formattedImages" :key="image.path"
              class="flex-shrink-0 w-full h-full relative flex items-center justify-center bg-white">
              <div
                class="w-full h-full transform-gpu will-change-transform transition-transform duration-300 ease-out flex items-center justify-center bg-white"
                :style="getImageStyle(idx)">
                <NuxtImg :src="'cl/' + image.path" :alt="image.alt" :loading="currentIndex === idx ? 'eager' : 'lazy'"
                  fit="inside" background="white" :width="scale > 1 ? 1000 : 600" :height="scale > 1 ? 1000 : 600"
                  @load="() => handleImageLoad(image)" />
              </div>
            </div>
          </div>

          <!-- Zoom İndikatörü -->
          <div v-if="scale > 1"
            class="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-light">
            {{ Math.round(scale * 100) }}%
          </div>

          <!-- Zoom Tooltip -->
          <Transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 translate-y-4"
            enter-to-class="opacity-100 translate-y-0" leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 translate-y-4">
            <div v-if="showTooltip && isMobile"
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/75 backdrop-blur-sm text-white px-4 py-2 rounded-xl text-sm font-light flex items-center gap-2 whitespace-nowrap">
              <UIcon name="i-heroicons-hand-raised" class="w-4 h-4" />
              <span>Çift dokunarak yakınlaştırabilirsiniz</span>
            </div>
          </Transition>

          <!-- Gezinme Kontrolleri -->
          <div v-if="!isMobile && formattedImages.length > 1"
            class="absolute inset-y-0 w-full flex items-center justify-between px-4 pointer-events-none">
            <UButton color="white" variant="solid" icon="i-heroicons-chevron-left"
              class="rounded-full !bg-white/90 backdrop-blur-sm shadow-sm hover:!bg-white pointer-events-auto" :ui="{
                rounded: 'rounded-full',
                padding: { xs: 'p-2' },
                size: { xs: 'sm' }
              }" @click="prevImage" />
            <UButton color="white" variant="solid" icon="i-heroicons-chevron-right"
              class="rounded-full !bg-white/90 backdrop-blur-sm shadow-sm hover:!bg-white pointer-events-auto" :ui="{
                rounded: 'rounded-full',
                padding: { xs: 'p-2' },
                size: { xs: 'sm' }
              }" @click="nextImage" />
          </div>
        </div>
      </div>

      <!-- Thumbnail'lar (Masaüstü) -->
      <div v-if="formattedImages.length > 1"
        class="hidden md:flex gap-4 mt-4 overflow-x-auto pb-2 snap-x scrollbar-hide">
        <button v-for="(image, index) in formattedImages" :key="index"
          class="relative flex-shrink-0 w-20 aspect-square rounded-lg overflow-hidden transition-all snap-start ring-2 ring-gray-200"
          :class="[
            currentIndex === index
              ? 'ring-2 ring-primary scale-95'
              : 'opacity-60 hover:opacity-100'
          ]" @click="currentIndex = index">
          <NuxtImg :src="'cl/' + image.path" :alt="image.alt" loading="lazy" fit="inside" width="100" height="100"
            background="white" format="webp" quality="80" class=" transform-gpu" placeholder />
        </button>
      </div>

      <!-- Progress Pills ve Sayaç (Mobil) -->
      <div v-if="isMobile" class="absolute bottom-0 left-0 right-0 p-4 transition-opacity duration-300">
        <!-- Progress Pills ve Sayaç Container -->
        <div class="flex justify-between items-center">
          <!-- Progress Pills -->
          <div class="flex items-center gap-1.5">
            <div v-for="(_, idx) in formattedImages" :key="idx" class="h-1 rounded-full transition-all duration-300"
              :class="[
                currentIndex === idx
                  ? 'w-6 bg-orange-500'
                  : idx === currentIndex - 1 || idx === currentIndex + 1
                    ? 'w-1.5 bg-gray-200'
                    : 'w-1 bg-gray-200/60'
              ]" />
          </div>

          <!-- Sayaç -->
          <span class="text-xs font-light px-2 py-1 rounded-full text-gray-600 bg-gray-100/80 backdrop-blur-[2px]">
            {{ currentIndex + 1 }}/{{ formattedImages.length }}
          </span>
        </div>
      </div>

      <!-- Zoom İkonu -->
      <UButton v-if="!isMobile" color="gray" variant="solid"
        :icon="scale > 1 ? 'i-heroicons-minus-small' : 'i-heroicons-plus-small'"
        class="absolute top-4 right-4 rounded-full !bg-gray-100/90 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
        :ui="{ rounded: 'rounded-full' }" @click="toggleZoom" />
    </div>
  </div>
</template>

<script setup>
import { useDebounceFn, useThrottleFn } from '@vueuse/core'

const props = defineProps({
  images: {
    type: Array,
    required: true,
    default: () => []
  }
})

// State yönetimi
const currentIndex = ref(0)
const loading = ref(true)
const touchStartX = ref(0)
const touchStartY = ref(0)
const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const startTouches = ref([])
const initialDistance = ref(0)
const initialScale = ref(1)
const lastTapTime = ref(0)
const showTooltip = ref(true)
const dragOffset = ref(0)
const loadedImages = ref(new Set()) // Yüklenen görselleri takip etmek için

// Tooltip'i gizle
onMounted(() => {
  setTimeout(() => {
    showTooltip.value = false
  }, 3000)
})

// Mobil kontrolü için useDevice composable'ı
const { isMobile } = useDevice()

// Görsel formatını düzenleme
const formattedImages = computed(() => {
  return (
    props.images?.map((image) => ({
      path: image.path,
      alt: 'Ürün Görseli',
      isLoaded: loadedImages.value.has(image.path)
    })) || []
  )
})

// Görsel dizisi değiştiğinde index'i sıfırla
watch(() => props.images, () => {
  currentIndex.value = 0
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
  dragOffset.value = 0
}, { deep: true })

// Aktif görsel
const activeImage = computed(() => formattedImages.value[currentIndex.value])

// Önceki ve sonraki görselleri preload et
watch(activeImage, (newImage) => {
  if (!newImage) return

  // Önceki görsel
  if (currentIndex.value > 0) {
    const prevImage = formattedImages.value[currentIndex.value - 1]
    preloadImage(prevImage.path)
  }

  // Sonraki görsel
  if (currentIndex.value < formattedImages.value.length - 1) {
    const nextImage = formattedImages.value[currentIndex.value + 1]
    preloadImage(nextImage.path)
  }
})

// Görsel yükleme yardımcı fonksiyonu
const preloadImage = (path) => {
  if (loadedImages.value.has(path)) return

  const img = new Image()
  img.onload = () => {
    loadedImages.value.add(path)
  }
  img.src = 'cl/' + path
}

// Debounced image load handler
const handleImageLoad = useDebounceFn((image) => {
  if (!image) return
  loadedImages.value.add(image.path)
  loading.value = false
}, 100)

// Görsel değişiminde loading state'i güncelle
watch(currentIndex, () => {
  const currentImage = formattedImages.value[currentIndex.value]
  loading.value = currentImage && !currentImage.isLoaded
})

// Component unmount olduğunda temizlik
onUnmounted(() => {
  loadedImages.value.clear()
})

// Görsel container referansı
const galleryContainer = ref(null)

// Performans için computed styles
const sliderStyle = computed(() => ({
  transform: `translateX(calc(-${currentIndex.value * 100}% + ${dragOffset.value || 0}px))`,
  touchAction: scale.value > 1 ? 'pan-x pan-y' : 'pan-y',
  transition: isDragging.value
    ? 'none'
    : 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)'
}))

const getImageStyle = (idx) => {
  if (currentIndex.value !== idx) return {}

  return {
    transform: `scale(${scale.value}) translate3d(${translateX.value}px, ${translateY.value}px, 0)`,
    transformOrigin: 'center',
    transitionProperty: isZooming.value ? 'none' : 'transform',
    transitionDuration: isDragging.value ? '0ms' : '300ms',
    transitionTimingFunction: 'ease-out',
    willChange: 'transform',
    backfaceVisibility: 'hidden',
    objectFit: 'contain',
    width: '100%',
    height: '100%'
  }
}

// Performans optimizasyonları için state
const isDragging = ref(false)
const isZooming = ref(false)
const touchMoveThrottle = ref(null)

// Throttled touch move handler
const handleTouchMove = useThrottleFn((e) => {
  // Eğer scale 1'den büyükse ve zoom yapılmışsa, sayfanın scroll'unu engelle
  if (scale.value > 1) {
    if (e.cancelable) {
      e.preventDefault()
    }
  }

  if (isZooming.value) return

  if (e.touches.length === 2) {
    handlePinchZoom(e)
  } else if (e.touches.length === 1) {
    handleDragMove(e)
  }
}, 16) // ~60fps

// Touch event handlers
const handleTouchStart = (e) => {
  // Sadece zoom durumunda prevent default yap
  if (scale.value > 1 && e.cancelable) {
    e.preventDefault()
  }

  isDragging.value = true
  const currentTime = Date.now()
  const timeDiff = currentTime - lastTapTime.value

  if (e.touches.length === 2) {
    isZooming.value = true
    startTouches.value = [...e.touches]
    initialDistance.value = getDistance(e.touches[0], e.touches[1])
    initialScale.value = scale.value
  } else if (e.touches.length === 1) {
    // Double tap kontrolü (300ms içinde)
    if (timeDiff < 300) {
      handleDoubleTap(e)
    } else {
      touchStartX.value = e.touches[0].clientX
      touchStartY.value = e.touches[0].clientY
      dragOffset.value = 0
    }
  }

  lastTapTime.value = currentTime
}

const handleDragMove = (e) => {
  if (scale.value <= 1) {
    const touch = e.touches[0]
    const diff = touch.clientX - touchStartX.value
    dragOffset.value = diff

    // Sınırları kontrol et ve direnç ekle
    if (
      (currentIndex.value === 0 && diff > 0) ||
      (currentIndex.value === formattedImages.value.length - 1 && diff < 0)
    ) {
      dragOffset.value = diff * 0.2
    }
  } else {
    handlePanMove(e)
  }
}

const handlePinchZoom = (e) => {
  const currentDistance = getDistance(e.touches[0], e.touches[1])
  const pinchScale = currentDistance / initialDistance.value
  scale.value = Math.min(Math.max(initialScale.value * pinchScale, 1), 3)

  if (scale.value > 1) {
    updateZoomPosition(e.touches[0], e.touches[1])
  }
}

const handleTouchEnd = (e) => {
  // Sadece zoom durumunda prevent default yap
  if (scale.value > 1 && e.cancelable) {
    e.preventDefault()
  }

  isDragging.value = false
  isZooming.value = false

  if (scale.value <= 1 && Math.abs(dragOffset.value) > 50) {
    if (
      dragOffset.value < 0 &&
      currentIndex.value < formattedImages.value.length - 1
    ) {
      currentIndex.value++
    } else if (dragOffset.value > 0 && currentIndex.value > 0) {
      currentIndex.value--
    }
  }

  dragOffset.value = 0
  startTouches.value = []

  if (scale.value < 1.1) {
    resetZoom()
  }
}

// Yardımcı metodlar
const handleDoubleTap = (e) => {
  e.preventDefault()
  const touch = e.touches[0]

  if (scale.value > 1) {
    isZooming.value = true
    resetZoom()
    setTimeout(() => {
      isZooming.value = false
    }, 50)
  } else {
    isZooming.value = true
    scale.value = 2
    if (galleryContainer.value) {
      updateZoomPosition(touch)
    }
    setTimeout(() => {
      isZooming.value = false
    }, 50)
  }
}

const handlePanMove = (e) => {
  const touch = e.touches[0]
  const diffX = touch.clientX - touchStartX.value
  const diffY = touch.clientY - touchStartY.value

  if (!galleryContainer.value) return

  const containerRect = galleryContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height

  // Görsel boyutlarını hesapla
  const scaledWidth = containerWidth * scale.value
  const scaledHeight = containerHeight * scale.value

  // Maksimum kayma mesafesini hesapla
  const maxTranslateX = Math.max(0, (scaledWidth - containerWidth) / 2)
  const maxTranslateY = Math.max(0, (scaledHeight - containerHeight) / 2)

  const sensitivity = 1.0

  // Yeni pozisyonları hesapla
  const newTranslateX = translateX.value + diffX * sensitivity
  const newTranslateY = translateY.value + diffY * sensitivity

  // Sınırları kontrol et ve uygula
  translateX.value = Math.min(
    Math.max(newTranslateX, -maxTranslateX),
    maxTranslateX
  )
  translateY.value = Math.min(
    Math.max(newTranslateY, -maxTranslateY),
    maxTranslateY
  )

  // Her harekette başlangıç noktasını güncelle
  touchStartX.value = touch.clientX
  touchStartY.value = touch.clientY
}

const updateZoomPosition = (touch1, touch2 = null) => {
  if (!galleryContainer.value) return

  const rect = galleryContainer.value.getBoundingClientRect()
  const containerWidth = rect.width
  const containerHeight = rect.height

  let centerX, centerY

  if (touch2) {
    centerX = (touch1.clientX + touch2.clientX) / 2
    centerY = (touch1.clientY + touch2.clientY) / 2
  } else {
    centerX = touch1.clientX
    centerY = touch1.clientY
  }

  // Görsel boyutlarını hesapla
  const scaledWidth = containerWidth * scale.value
  const scaledHeight = containerHeight * scale.value

  // Maksimum kayma mesafesini hesapla
  const maxTranslateX = Math.max(0, (scaledWidth - containerWidth) / 2)
  const maxTranslateY = Math.max(0, (scaledHeight - containerHeight) / 2)

  const centerOffsetX = containerWidth / 2 - (centerX - rect.left)
  const centerOffsetY = containerHeight / 2 - (centerY - rect.top)

  // Sınırları kontrol et ve uygula
  translateX.value = Math.min(
    Math.max(centerOffsetX * 0.8, -maxTranslateX),
    maxTranslateX
  )
  translateY.value = Math.min(
    Math.max(centerOffsetY * 0.8, -maxTranslateY),
    maxTranslateY
  )
}

// Zoom yardımcı fonksiyonları
const getDistance = (touch1, touch2) => {
  const dx = touch1.clientX - touch2.clientX
  const dy = touch1.clientY - touch2.clientY
  return Math.sqrt(dx * dx + dy * dy)
}

const resetZoom = () => {
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
}

// Navigasyon metodları
const nextImage = () => {
  if (scale.value > 1) return
  if (formattedImages.value.length <= 1) return
  if (currentIndex.value < formattedImages.value.length - 1) {
    resetZoom()
    currentIndex.value++
  }
}

const prevImage = () => {
  if (scale.value > 1) return
  if (formattedImages.value.length <= 1) return
  if (currentIndex.value > 0) {
    resetZoom()
    currentIndex.value--
  }
}

// Zoom butonu için
const toggleZoom = () => {
  isZooming.value = true
  if (scale.value > 1) {
    resetZoom()
  } else {
    scale.value = 2
  }
  setTimeout(() => {
    isZooming.value = false
  }, 50)
}

// Zoom durumunu izle ve touch kontrolünü güncelle
watch(scale, (newScale) => {
  if (galleryContainer.value) {
    galleryContainer.value.setAttribute(
      'data-zoomed',
      newScale > 1 ? 'true' : 'false'
    )
  }
})
</script>

<style scoped>
.transform-gpu {
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  perspective: 1000;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
